<template>
  <div
    class="chart-container anim-no-events_500 gw_hvr"
    :class="[{ 'no-events': !actions.length }, { 'chart-fre_fullscreen': !hasFullscreen }]"
    data-tst="freq-chart"
  >
    <ChartLegend
      v-if="hasLegend"
      class="gw_hvr_content"
      style="top: 0rem; left: 1.5rem; z-index: 1"
      :hasFullscreen="hasFullscreen"
      @goFullscreen="goFullscreen()"
    />
    <portal
      v-if="hasFullscreen"
      to="freqchartdialog"
    >
      <StudyFrequencyChart
        class="chart-fre-dialog"
        :freKey="freKey"
        :hasFullscreen="false"
        :actions="actions"
        :hasBreakdown="hasBreakdown"
        :catDraws="catDraws"
        :catHands="catHands"
        :filterActions="filterActions"
        :showBetsIn="showBetsIn"
        @action:click="action => toggleAction(action)"
      />
    </portal>

    <div
      class="chart_inner no-scroll-track"
      @mouseleave="hasTooltip = false"
      @mouseenter="hasTooltip = true"
      @mousemove="handleMousemove"
      @touchmove="handleMousemove"
    >
      <div
        v-if="hasTooltip && hoveredAction.code"
        ref="tooltip"
        class="f-center weight_600 gw_box freq-chart_tooltip anim-fade-in"
        style="--box-padding: 0.5em 1em; min-width: 14rem"
        :style="`left:${cursorX}px; --flip-transform: translate(${
          flipTooltip ? '-101% ' : '1%'
        },0)`"
      >
        <div
          :class="`chart-tooltip-circle mr-2 ${hoveredAction.css_class}`"
          :style="`${hoveredAction.group_display_name ? '' : `background:${hoveredAction.hex}`}`"
        ></div>
        <div class="text-nowrap clr-white">
          {{
            hoveredAction.group_display_name
              ? $t(`browse.${hoveredAction.group_display_name}`)
              : hoveredAction.display_name
          }}
          <span
            v-if="!hoveredAction.group_display_name"
            v-html="getBetHtml({ showBetsIn, a: hoveredAction })"
          ></span>
        </div>
        <div class="ml-auto pl-12 clr-white">
          <div class="text-right text-nowrap">
            {{ numFixedUnit(hoveredAction[freKey] * 100, 'strategy') }}%
          </div>
          <div
            v-if="hasEv"
            class="text-right text-nowrap"
          >
            {{ numFixedUnit(hoveredAction.ev, 'ev') }} EV
          </div>
        </div>
      </div>
      <ChartAxisY :hasTicks="true" />
      <div
        ref="cont"
        class="freq-chart"
      >
        <div
          v-for="(action, index) in actions"
          :key="index + 'a'"
          class="freq-chart_column"
          :class="[
            { 'freq-chart_column_read-only': readOnly },
            {
              'freq-chart_column_selected': action.is_selected || action.is_preselected,
            },
            {
              'freq-chart_column_active': filterActions.some(i => i.code === action.code),
            },
          ]"
          @mouseenter="handleMouseenter(action)"
          @mouseleave="handleMouseleave"
          @click="toggleAction(action)"
        >
          <div
            :class="`freq-chart_column_column ${action.css_class}`"
            :style="`height:${action[freKey] * 100}%; ${
              action.group_display_name ? '' : `background:${action.hex}`
            }`"
          >
            <div
              v-if="action[freKey] > 0.05"
              class="freq-chart_column_column_value"
              :class="{ above90: action[freKey] > 0.9 }"
            >
              {{ numFixed(action[freKey] * 100) }}%
            </div>
          </div>
          <div class="freq-chart_column_legend">
            <span
              v-if="action.group_display_name"
              v-html="$t(`browse.${action.group_display_name}`)"
            >
            </span>
            <span
              v-else-if="action.type === 'RAISE'"
              v-html="
                (showBetsIn === 'pot' || showBetsIn === 'pot_and_bb') && action.amount_by_pot
                  ? action.amount_by_pot
                  : action.raise_amount
              "
            >
            </span>
            <span
              v-else
              v-html="action.display_name"
            ></span>
            <GwMenu
              v-if="hasBreakdown && action.total_combos > 0.001"
              ref="gwmenu"
              class="freq-chart_column_legend_breakdown"
              :flOptions="{
                placement: 'right-start',
                offset: -16,
              }"
              style="position: absolute; --opener-width: 1rem"
              trigger="hover"
            >
              <template #opener>
                <div class="freq-chart_breakdown">
                  <i class="mdi mdi-chart-box-outline freq-chart_column_legend_breakdown_icon"></i>
                </div>
              </template>
              <div
                class="gw_menu"
                style="--width: auto; padding: 0.75rem 0"
              >
                <StudyComboBreakdown
                  :action="action"
                  :catDraws="catDraws"
                  :catHands="catHands"
                />
              </div>
            </GwMenu>
          </div>
        </div>
      </div>
      <div
        class="position-absolute d-flex"
        style="left: -1.375em; top: 100%"
      >
        <span style="font-size: 0.8em">
          {{ showBetsIn === 'pot' || showBetsIn === 'pot_and_bb' ? '%' : 'BB' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import GwMenu from '@/components/lib/GwMenu'
import ChartAxisY from '@/components/Charts/common/ChartAxisY'
import ChartLegend from '@/components/Charts/common/ChartLegend'
import StudyComboBreakdown from '@/components/Study/StudyComboBreakdown'
import format_mix from '@/mixins/format_mix'
import float_mix from '@/mixins/float_mix'
import throttle from 'lodash/throttle'

export default {
  name: 'StudyFrequencyChart',
  mixins: [format_mix, float_mix],
  props: {
    actions: { default: () => [] },
    hasFullscreen: { type: Boolean, default: true },
    hasBreakdown: Boolean,
    readOnly: Boolean,
    hasEv: Boolean,
    freKey: { default: () => 'total_frequency' },
    hasLegend: { type: Boolean, default: true },
    filterActions: { default: () => [] },
    showBetsIn: String,
    action: Object,
    catDraws: Array,
    catHands: Array,
  },
  emits: ['action:click'],
  data() {
    return {
      cursorX: 0,
      hasTooltip: false,
      flipTooltip: false,
      hoveredAction: {},
    }
  },
  methods: {
    goFullscreen() {
      this.$store.dispatch('router_set_dialogs', 'chart-dialog_portaltarget-freqchartdialog')
    },
    toggleAction(action) {
      if (this.readOnly) return
      this.$emit('action:click', action)
    },
    handleMousemove: throttle(function (e) {
      if (!this.$refs.cont) return

      this.hasTooltip = true
      if (e?.touches) {
        e = {
          pageX: e?.touches[0]?.pageX,
          pageY: e?.touches[0]?.pageY,
        }
      }
      const res = e?.pageX - e?.currentTarget?.getBoundingClientRect()?.x || 0
      const cont = this.$refs.cont.getBoundingClientRect()
      this.cursorX = res < 0 || !e ? 0 : res > cont.width ? cont.width : res
      this.flipTooltip = res > cont.width / 2
    }, 1),
    handleMouseenter(action) {
      this.hoveredAction = action
      this.$store.commit('sol_filters_set', { key: 'actions_highlighted', value: [action] })
    },
    handleMouseleave() {
      this.$store.commit('sol_filters_set', { key: 'actions_highlighted', value: [] })
    },
  },
  computed: {
    globals_window_size() {
      return this.$store.getters.globals_window_size
    },
  },
  components: {
    StudyComboBreakdown,
    GwMenu,
    ChartAxisY,
    ChartLegend,
  },
}
</script>
<style lang="scss">
.chart-fre-dialog {
  height: calc(100% - 20px) !important;
  width: calc(100% - 32px) !important;
  .chart-legend {
    &:after {
      background: var(--clr-black-1) !important;
    }
  }

  .freq-chart_tooltip {
    --top: calc(50% - 2rem) !important;
    font-size: 24px;
  }
}
</style>

<style lang="scss" scoped>
@media (max-width: 1240px) {
  .chart-container:not(.chart-fre_fullscreen) {
    .freq-chart_column_legend {
      font-size: 0.6rem !important;
      padding-bottom: 2px;
    }
  }
}
.chart-container {
  position: absolute;
  height: 100%;
  display: flex;
  flex: 1;
  width: 100%;
  flex-shrink: 0;
  padding: 0.5em 0 1.375em 1.5em;
  // --clr-chart-back: rgba(255, 255, 255, 0.025);
  --clr-chart-back: transparent;
  flex-direction: column;
}
.chart {
  &_inner {
    display: flex;
    height: 100%;
    width: 100%;
    flex: 1;
    position: relative;
  }
}
.chart-tooltip-circle {
  height: 0.8em;
  width: 0.8em;
  border-radius: 50%;
  flex-shrink: 0;
}
.freq-chart {
  display: flex;
  height: 100%;
  width: 100%;
  flex: 1;
  position: relative;
  background: var(--clr-chart-back);
  border-radius: 0 4px 0 0;
  // &_breakdown {
  //   z-index: 2;
  //   position: absolute;
  //   width: 2rem;
  //   height: 2rem;
  //   background: red;
  //   &_icon {
  //     position: absolute;
  //     right: 0.25rem;
  //     opacity: 0.7;
  //     font-size: 1.25rem;
  //   }
  // }
  &_tooltip {
    --top: 1em;
    position: absolute;
    z-index: 2;
    transition: transform 0.22s;
    --flip-transform: translate(0, 0);
    transform: var(--flip-transform);
    top: var(--top) !important;
    box-shadow: var(--menu-shadow);
  }
  &_bet-average {
    position: absolute;
    top: -0.9em;
    right: 0;
    font-size: 0.8em;
    font-weight: 700;
    color: var(--clr-grey-2);
    text-shadow: 1px 0px 0px var(--clr-black-1);
    z-index: 2;
  }
  &_column {
    display: flex;
    flex-direction: column;
    display: flex;
    align-items: center;
    position: relative;
    height: 100%;
    margin-top: auto;
    min-height: 1px;
    margin: 0 0 0 1px;
    --b-radius: 8px;
    flex: 1;
    transition: 0.2s;
    cursor: pointer;
    &_read-only {
      cursor: default !important;
    }
    --clr-column-back-hover: var(--clr-black-1-2);
    --clr-column-back-active: var(--clr-black-1-2);
    &_active {
      background: var(--clr-column-back-active);
      border-radius: var(--b-radius) var(--b-radius) 0 0;
      .freq-chart_column_legend {
        background: var(--clr-column-back-active);
        border-radius: 0px 0px var(--b-radius) var(--b-radius);
      }
    }
    &_selected {
      background: var(--clr-column-back-active);
      border-radius: var(--b-radius) var(--b-radius) 0 0;
      .freq-chart_column_legend {
        background: var(--clr-column-back-active);
        border-radius: 0px 0px var(--b-radius) var(--b-radius);
      }
    }
    &:hover:not(.freq-chart_column_selected) {
      background: var(--clr-column-back-hover);
      border-radius: var(--b-radius) var(--b-radius) 0 0;
      .freq-chart_column_legend {
        background: var(--clr-column-back-hover);
        border-radius: 0px 0px var(--b-radius) var(--b-radius);
        &_breakdown {
          border-radius: 0px 0px var(--b-radius) var(--b-radius);
          opacity: 1;
        }
      }
    }
    &_column {
      border-top-right-radius: var(--b-radius);
      border-top-left-radius: var(--b-radius);
      margin: auto 0 0 0;
      transition: height 0.4s;
      will-change: height;
      width: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      //
      &_value {
        color: var(--clr-white-0);
        position: absolute;
        top: -1.5em;
        line-height: 1.2;
        padding: 0 0.25em;
        font-size: 0.7em;
        font-weight: 700;
        // font-weight: 600;
        // font-weight: 500;
        text-align: center;
        text-shadow: var(--text-shadow_drop);
        &.above90 {
          top: -0.5em;
        }
      }
    }
    &_legend {
      color: var(--clr-white-0);
      position: absolute;
      top: 100%;
      width: 100%;
      font-weight: 500;
      font-size: 0.875em;
      text-align: center;
      white-space: nowrap;
      display: flex;
      justify-content: center;
      &_breakdown {
        opacity: 0;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: inherit;
        &_icon {
          font-size: 1.125em;
        }
      }
    }
  }
}
</style>
